import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Background,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  Position,
  ReactFlow
} from '@xyflow/react';
import { Globe, Pencil } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import '@xyflow/react/dist/style.css';

import { LANGUAGES } from '~/components/features/communications/constants';
import { PageContainer } from '~/components/layouts/page-container';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { communicationsQueryOptions } from '~/lib/communications';
import { resolveSlugIds } from '~/lib/slug-resolver';

const commSearchSchema = z.object({
  lang: z.string().length(2).default('en')
});

export const Route = createFileRoute(
  '/_authenticated/org/$orgSlug/department/$deptSlug/communications/'
)({
  validateSearch: commSearchSchema,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ context, params, deps }) => {
    const { orgId, deptId } = resolveSlugIds(context.queryClient, params.orgSlug, params.deptSlug);
    if (orgId && deptId) {
      void context.queryClient.prefetchQuery(communicationsQueryOptions(orgId, deptId, deps.lang));
    }
  },
  component: CommunicationsPage
});

interface FlowNodeData extends Record<string, unknown> {
  title: string;
  channel: string;
  preview?: string;
  editable?: boolean;
  editTo?: string;
  editParams?: Record<string, string>;
  editSearch?: Record<string, string>;
  placeholder?: boolean;
}

function FlowNodeComponent({ data }: NodeProps<Node<FlowNodeData>>): React.ReactNode {
  const { t } = useTranslation();
  const { title, channel, preview, editable, editTo, editParams, editSearch, placeholder } = data;

  return (
    <>
      <Handle type="target" position={Position.Top} className="!opacity-0" />
      <Card
        className="w-60 rounded-xl border border-slate-200 bg-card p-4 shadow-xs"
        style={{ pointerEvents: 'all' }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-sm font-semibold text-foreground">{title}</span>
            <span className="text-xs text-muted-foreground">{channel}</span>
            {preview && (
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{preview}</p>
            )}
            {placeholder && (
              <Badge className="mt-1 w-fit border-0 bg-slate-100 text-slate-500 text-xs">
                {t('communications.nodes.comingSoon')}
              </Badge>
            )}
          </div>
          {editable && editTo && (
            <Button
              variant="outline"
              size="sm"
              render={<Link to={editTo} params={editParams} search={editSearch} />}
            >
              <Pencil className="size-3.5" />
              {t('communications.actions.edit')}
            </Button>
          )}
        </div>
      </Card>
      <Handle type="source" position={Position.Bottom} className="!opacity-0" />
    </>
  );
}

function JunctionNode(_props: NodeProps): React.ReactNode {
  return (
    <>
      <Handle type="target" position={Position.Top} className="!opacity-0" />
      <div className="size-3 rounded-full bg-slate-400 ring-2 ring-white" />
      <Handle type="source" position={Position.Bottom} id="main" className="!opacity-0" />
      <Handle type="source" position={Position.Right} id="branch" className="!opacity-0" />
    </>
  );
}

const nodeTypes = { flowNode: FlowNodeComponent, junction: JunctionNode };

function CommunicationsPage(): React.ReactNode {
  const { t } = useTranslation();
  const { orgSlug, deptSlug } = Route.useParams();
  const { lang } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);

  const { data } = useQuery(communicationsQueryOptions(orgId, deptId ?? '', lang));

  const editParams = useMemo(() => ({ orgSlug, deptSlug }), [orgSlug, deptSlug]);
  const editSearch = useMemo(() => ({ lang }), [lang]);

  const nodes: Node[] = useMemo(
    () => [
      {
        id: 'invite',
        type: 'flowNode',
        position: { x: 0, y: 0 },
        data: {
          title: t('communications.invite.title'),
          channel: t('communications.nodes.emailsAndSms'),
          preview: data?.invite.sms.body,
          editable: true,
          editTo: '/org/$orgSlug/department/$deptSlug/communications/$type',
          editParams: { ...editParams, type: 'invite' },
          editSearch
        }
      },
      {
        id: 'junction',
        type: 'junction',
        position: { x: 114, y: 180 },
        data: {}
      },
      {
        id: 'intro',
        type: 'flowNode',
        position: { x: 0, y: 240 },
        data: {
          title: t('communications.nodes.interviewIntro'),
          channel: t('communications.nodes.voiceAndChat'),
          placeholder: true
        }
      },
      {
        id: 'outro',
        type: 'flowNode',
        position: { x: 0, y: 410 },
        data: {
          title: t('communications.nodes.interviewOutro'),
          channel: t('communications.nodes.voiceAndChat'),
          placeholder: true
        }
      },
      {
        id: 'reminder',
        type: 'flowNode',
        position: { x: 340, y: 240 },
        data: {
          title: t('communications.reminder.title'),
          channel: t('communications.nodes.emailsAndSms'),
          preview: data?.reminder.sms.body,
          editable: true,
          editTo: '/org/$orgSlug/department/$deptSlug/communications/$type',
          editParams: { ...editParams, type: 'reminder' },
          editSearch
        }
      }
    ],
    [t, data, editParams, editSearch]
  );

  const edges: Edge[] = useMemo(
    () => [
      {
        id: 'invite-junction',
        type: 'step',
        source: 'invite',
        target: 'junction',
        style: { stroke: '#94a3b8' },
        animated: false
      },
      {
        id: 'junction-intro',
        type: 'step',
        source: 'junction',
        sourceHandle: 'main',
        target: 'intro',
        style: { stroke: '#94a3b8' },
        animated: false
      },
      {
        id: 'intro-outro',
        type: 'step',
        source: 'intro',
        target: 'outro',
        style: { stroke: '#94a3b8' },
        animated: false
      },
      {
        id: 'junction-reminder',
        type: 'step',
        source: 'junction',
        sourceHandle: 'branch',
        target: 'reminder',
        style: { stroke: '#cbd5e1', strokeDasharray: '5 5' },
        animated: false
      }
    ],
    []
  );

  const onLangChange = useCallback(
    (value: string) => void navigate({ search: (_prev) => ({ lang: value }) }),
    [navigate]
  );

  return (
    <PageContainer
      title={t('communications.title')}
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger render={<Button variant="outline" size="sm" />}>
            <Globe className="size-4" />
            {LANGUAGES.find((l) => l.code === lang)?.label ?? lang.toUpperCase()}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup value={lang} onValueChange={onLangChange}>
              {LANGUAGES.map(({ code, label }) => (
                <DropdownMenuRadioItem key={code} value={code}>
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <div className="h-[700px] w-full">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#e2e8f0" />
        </ReactFlow>
      </div>
    </PageContainer>
  );
}
